export type TrialBalancePlatform = {
  platformId: string
  name: string
  companyId: string
  availableDates: string[]
}

export enum ComplianceJobStatus {
  Pending = 'Pending',
  Processing = 'Processing',
  Reviewing = 'Reviewing',
  Parsed = 'Parsed',
  NeedsReview = 'Needs Review',
  AllMatched = 'All Matched',
  Failed = 'Failed',
  Reviewed = 'Reviewed'
}

export type ComplianceJobListItem = {
  id: string
  orgId: string | null
  periodEndDate: string
  uploadDate: string
  status: ComplianceJobStatus
}

export type ComplianceJobsResponse = {
  items: ComplianceJobListItem[]
  total: number
}

export type ComplianceJobStatusResponse = {
  jobId: string
  orgId: string | null
  status: ComplianceJobStatus
  errorMessage: string | null
  lastUpdated: string
  createdAt: string
  periodEndDate?: string
}

export type CreateComplianceJobResponse = {
  jobId: string
  clientId: string
  orgId: string | null
  periodEndDate: string
  status: ComplianceJobStatus
  createdAt: string
}

export type XeroTrialBalanceRow = {
  account: string
  ytdDebit: number | null
  ytdCredit: number | null
}

export type WorkpaperFileResult = {
  fileId: string
  fileName: string
  size: number
  sheets?: string[]
  isReplaced: boolean
}

export type UploadJobFilesResponse = {
  success: boolean
  s3Key?: string
  errorMessage?: string
  files?: WorkpaperFileResult[]
}

export type ComplianceUploadFileRequest = {
  fileName: string
  contentType: string
  size: number
}

export type ComplianceUploadFileDescriptor = {
  fileId: string
  fileName: string
  contentType: string
  size: number
  s3Key: string
  uploadUrl: string
  method: string
}

export type CreateComplianceUploadSessionRequest = {
  jobId: string
  orgId?: string
  trialBalanceSource?: string
  indexFile?: ComplianceUploadFileRequest | null
  workpaperFiles: ComplianceUploadFileRequest[]
}

export type CreateComplianceUploadSessionResponse = {
  uploadSessionId: string
  expiresAtUtc: string
  indexFile?: ComplianceUploadFileDescriptor | null
  workpaperFiles: ComplianceUploadFileDescriptor[]
}

export type CompleteComplianceUploadSessionRequest = {
  jobId: string
  orgId?: string
  trialBalanceSource?: string
  indexFile?: ComplianceUploadFileDescriptor | null
  workpaperFiles: ComplianceUploadFileDescriptor[]
}

export type ComplianceAssetReviewItem = {
  complianceAssetId: string
  assetName: string
  amount: string
  workpaperAmount: string
  matchedWorkpaperFileName: string
  status?: ComplianceComputedStatus | null
  sheetName?: string | null
  matchedWorkpaperSheets?: string[] | null
  class?: string | null
  reviewStatus?: AssetReviewStatus | null
  reviewNotes?: string | null
  reviewUpdatedAt?: string | null
}

export enum AssetReviewStatus {
  Reviewed = 'reviewed',
  NeedsReview = 'needsReview',
  Locked = 'locked',
}

export enum ComplianceComputedStatus {
  Match = 'Match',
  Mismatch = 'Mismatch',
  Missing = 'Missing',
}

export type AssetReviewState = {
  status?: AssetReviewStatus
  notes?: string
}

export type UpdateAssetReviewRequest = {
  status?: AssetReviewStatus | null
  notes?: string | null
}

export type UpdateAssetReviewResponse = {
  success: boolean
  complianceAssetId: string
  jobStatus?: ComplianceJobStatus | null
  status?: AssetReviewStatus | null
  notes?: string | null
  updatedAt: string
}

export type BulkUpdateAssetReviewRequest = {
  complianceAssetIds: string[]
  status?: AssetReviewStatus | null
  notes?: string | null
}

export type BulkUpdateAssetReviewResponse = {
  success: boolean
  jobStatus?: ComplianceJobStatus | null
  updatedCount: number
  failedCount: number
  failedIds?: string[]
}

export type ReanalyzeJobResponse = {
  success: boolean
  processedAssetIds: string[]
  newMatches: number
  status: string
  errorMessage: string | null
}

export type ComplianceLogItem = {
  complianceLogId: string
  message: string
  level: string
  step: string
  createdAt: string
}

export type ComplianceLogsResponse = {
  logs: ComplianceLogItem[]
  status: string
}
